/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useMemo } from "react";
import { CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { useGetDasInventoryQuery } from "../../../store/api/devices/deviceApiSlice";
import { parseDeviceAttributes, isBitSet, getAlarmLedColor } from "../../../components/Drawers/DasDeviceDetails/utils";
import DetailRow from "../../../components/Drawers/DasDeviceDetails/components/DetailRow";
import AlarmTable from "../../../components/Drawers/DasDeviceDetails/components/AlarmTable";
import { AlarmDefinition } from "../../../components/Drawers/DasDeviceDetails/types";
import { resolveDeviceStatusLabel } from "../../../utils/deviceStatus";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";

const HcmInfo: React.FC = () => {
  const location = useLocation();
  const selectedDevice = useSelector(selectedDeviceSelector);
  const deviceObjectKey = selectedDevice?.objectKey || location.pathname.split("/")[2];

  // Fetch DAS inventory to get HCM device
  const { data: dasInventory = [], isLoading } = useGetDasInventoryQuery(
    { deviceKey: deviceObjectKey },
    { 
      skip: !deviceObjectKey,
      pollingInterval: 30000, // Refresh every 30 seconds
    }
  );

  // Find HCM device
  const hcmDevice = useMemo(() => {
    return dasInventory.find((device) => device.equipmentType === "HCM");
  }, [dasInventory]);

  // Parse HCM attributes
  const parsedAttributes = useMemo(() => {
    if (!hcmDevice?.attributes) return {};
    return parseDeviceAttributes(hcmDevice.attributes);
  }, [hcmDevice]);

  const statusLabel = hcmDevice ? resolveDeviceStatusLabel(hcmDevice.status) : "Unknown";
  const isDisconnected = statusLabel === "Disconnected";

  // HCM Alarm definitions from MIB (bits 0-4, 0-indexed)
  const alarmDefinitions: AlarmDefinition[] = [
    { bit: 0, name: "HW Failure" },
    { bit: 1, name: "Adjustment Fault" },
    { bit: 2, name: "Installation Fault" },
    { bit: 3, name: "SW Release Mismatch" },
    { bit: 4, name: "Connectivity" },
  ];

  // Check if any alarms are active
  const hasActiveAlarms = useMemo(() => {
    if (parsedAttributes.alarms === undefined) return false;
    return alarmDefinitions.some(alarm => isBitSet(parsedAttributes.alarms, alarm.bit));
  }, [parsedAttributes.alarms, alarmDefinitions]);

  // Get overall status LED color
  const statusLedColor = getAlarmLedColor(isDisconnected, hasActiveAlarms);

  // Format AGC switch value
  const formatAgcMode = (value: any) => {
    if (value === undefined || value === null) return "N/A";
    const val = String(value);
    switch (val) {
      case "0":
        return "Off";
      case "1":
        return "On";
      default:
        return val;
    }
  };

  // Format DHCP mode
  const formatDhcpMode = (value: any) => {
    if (value === undefined || value === null) return "N/A";
    const val = String(value);
    switch (val) {
      case "0":
        return "Static IP";
      case "1":
        return "DHCP";
      case "2":
        return "Auto IP";
      case "3":
        return "DHCP with fallback";
      case "4":
        return "Stateless DHCPv6";
      case "5":
        return "Stateful DHCPv6";
      default:
        return val;
    }
  };

  // Format TDD configuration
  const formatTddConfig = (value: any) => {
    if (value === undefined || value === null) return "N/A";
    const val = String(value);
    switch (val) {
      case "0":
        return "Configuration 0";
      case "1":
        return "Configuration 1";
      case "2":
        return "Configuration 2";
      case "3":
        return "Configuration 3";
      case "4":
        return "Configuration 4";
      case "5":
        return "Configuration 5";
      case "6":
        return "Configuration 6";
      default:
        return val;
    }
  };

  // Format TDD CP Type
  const formatTddCpType = (value: any) => {
    if (value === undefined || value === null) return "N/A";
    const val = String(value);
    switch (val) {
      case "0":
        return "Normal";
      case "1":
        return "Extended";
      default:
        return val;
    }
  };

  // Format TDD Center Frequency
  const formatTddCenterFreq = (value: any) => {
    if (value === undefined || value === null) return "N/A";
    const val = String(value);
    switch (val) {
      case "2526":
        return "2526 MHz (Low band)";
      case "2593":
        return "2593 MHz (Medium band)";
      case "2660":
        return "2660 MHz (High band)";
      default:
        return val ? `${val} MHz` : "N/A";
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <CircularProgress size="4rem" color="primary" />
      </div>
    );
  }

  if (!hcmDevice) {
    return (
      <div className="p-4">
        <p className="text-sm text-muted-foreground">No HCM device found in this MA3000 system.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Local IP Settings */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Local IP Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <DetailRow label="Local DHCP Mode" value={formatDhcpMode(parsedAttributes.getLocalDHCP)} />
          <DetailRow label="Local IP Address" value={parsedAttributes.getLocalIPAddress} />
          <DetailRow label="Local Subnet Mask" value={parsedAttributes.getLocalSubMask} />
        </CardContent>
      </Card>

      {/* LAN IP Settings */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">LAN IP Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <DetailRow label="IP Address" value={hcmDevice.ipAddress} />
          <DetailRow label="MAC Address" value={hcmDevice.macAddress} />
          <DetailRow label="LAN DHCP Mode" value={formatDhcpMode(parsedAttributes.getLanDHCP)} />
          <DetailRow label="LAN IP Address" value={parsedAttributes.getLanIPAddress} />
          <DetailRow label="LAN Subnet Mask" value={parsedAttributes.getLanSubMask} />
          <DetailRow label="LAN Default Gateway" value={parsedAttributes.getLanDefaultGateway} />
          <DetailRow label="LAN IPv6 DHCP Mode" value={formatDhcpMode(parsedAttributes.getLanV6DHCP)} />
          <DetailRow label="LAN IPv6 Address" value={parsedAttributes.getLanIPv6Address} />
          <DetailRow label="LAN IPv6 Subnet Mask" value={parsedAttributes.getLanV6SubMask} />
        </CardContent>
      </Card>

      {/* Settings */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <DetailRow label="AGC Mode" value={formatAgcMode(parsedAttributes.agcSwitch)} />
        </CardContent>
      </Card>

      {/* TDD Configuration */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">TDD Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <DetailRow
            label="TDD Allocation Mode"
            value={formatTddConfig(parsedAttributes.hcmTDDAllocationMode || parsedAttributes.tddAllocationMode || parsedAttributes.tddConfig)}
          />
          <DetailRow
            label="TDD CP Type"
            value={formatTddCpType(parsedAttributes.hcmTDDCpType || parsedAttributes.tddCpType)}
          />
          <DetailRow
            label="TDD Sub Frame Allocation"
            value={parsedAttributes.hcmTDDSubFrameAllocation || parsedAttributes.tddSubFrameAllocation || "N/A"}
          />
          <DetailRow
            label="TDD Center Frequency"
            value={formatTddCenterFreq(parsedAttributes.hcmTDDCenterFreq || parsedAttributes.tddCenterFreq)}
          />
        </CardContent>
      </Card>

      {/* Alarms */}
      {(parsedAttributes.alarms !== undefined || parsedAttributes.alarmsMask !== undefined) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Alarms</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm font-semibold text-foreground">Overall Status: {statusLabel}</span>
              <span
                className="inline-block w-4 h-4 rounded-full shrink-0"
                style={{ backgroundColor: statusLedColor, boxShadow: `0 0 8px ${statusLedColor}` }}
              />
            </div>
            <AlarmTable
              alarms={parsedAttributes.alarms}
              alarmsMask={parsedAttributes.alarmsMask}
              alarmDefinitions={alarmDefinitions}
              isDisconnected={isDisconnected}
            />
            <div className="mt-4 pt-3 border-t border-border">
              <DetailRow label="Band" value={hcmDevice.band} />
              <DetailRow label="Location" value={hcmDevice.location} />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default HcmInfo;
